import { SupabaseClient } from '@supabase/supabase-js'
import { Database } from "@/supabase/types"
import { PermAction, PermOptions, permOptionsKey, TableWithPermissions } from '@/types/base/permissions'
import { Json, TablesInsert, TablesUpdate } from "@/supabase/types"
import Authuser, { authCanPerform, evaluatePermissions, Role } from "@/types/base/authuser"
import { TableType } from "@/types/base/enums"
import { createIsomorphicLogger } from '@/utils/logger/isomorphicLogger'
import { logError } from '@/utils/errorLogging'
import { isAidonAdmin } from './tenants'
import { getProfileByUserId } from './profiles'

const logger = createIsomorphicLogger('db.workspaces')

interface WorkspaceMetadata {
  tenant_shared?: boolean
	root_workspace?: boolean
}

export const getHomeWorkspaceByUserId = async (supabase: SupabaseClient<Database>, userId: string) => {
	// eslint-disable-next-line prefer-const
  let { data: homeWorkspace, error } = await supabase
    .from("workspaces")
    .select("*")
    .eq("user_id", userId)
    .eq("is_home", true)
    .single()

	if (error) {
		logError(logger, error, `Error in getHomeWorkspaceByUserId for userId:${userId}`)
		homeWorkspace = null
	}
	else if (!homeWorkspace) {
		logger.warn(`No home Workspace found for userId:${userId}`)
	}

  return homeWorkspace
}

export const getTenantHomeWorkspace = async (supabase: SupabaseClient<Database>, tenantId: string) => {
	// eslint-disable-next-line prefer-const
  let { data: workspaces, error } = await supabase
    .from("workspaces")
    .select("*")
		.eq("tenant_id", tenantId)
    .eq("is_home", true)

	if (error) {
		logError(logger, error, `Error in getTenantHomeWorkspace for tenantId:${tenantId}`)
		workspaces = []
	}
	else if (!workspaces || workspaces.length === 0) {
		logger.warn(`No tenant home Workspace found for tenantId:${tenantId}`)
		workspaces = []
	}

	const sharedWorkspace = workspaces.find(w => isTenantShared(w))
	return sharedWorkspace || null
}

export const getUserDefaultWorkspace = async (supabase: SupabaseClient<Database>, userId: string) => {
	const profile = await getProfileByUserId(supabase, userId)
	if (profile && profile.default_workspace) {
		return (await getWorkspaceById(supabase, profile.default_workspace))
	}
  return (await getHomeWorkspaceByUserId(supabase, userId))
}

export const getHomeWorkspaceIdByUserId = async (supabase: SupabaseClient<Database>, userId: string) => {
  return (await getHomeWorkspaceByUserId(supabase, userId))?.id
}

export const getWorkspaceById = async (supabase: SupabaseClient<Database>, workspaceId: string) => {
	// eslint-disable-next-line prefer-const
  let { data: workspace, error } = await supabase
    .from("workspaces")
    .select("*")
    .eq("id", workspaceId)
    .single()

	if (error) {
		logError(logger, error, `Error in getWorkspaceById for workspaceId:${workspaceId}`)
		workspace = null
	}
	else if (!workspace) {
		logger.warn(`No Workspace found for workspaceId:${workspaceId}`)
	}

  return workspace
}

//Returns the workspaces that the user is allowed to select based on
export const getUserWorkspacesByTenantId = async (supabase: SupabaseClient<Database>, tenantId: string,
		aidonAdmin:boolean) => {
	if (aidonAdmin) {
		// eslint-disable-next-line prefer-const
		let { data: workspaces, error } = await supabase
			.from("workspaces")
			.select("*")
			.order("created_at", { ascending: false })
//			.filter('metadata', 'cs', '{"tenant_shared": true}')  //this is the same as isTenantShared below

		if (error) {
			logError(logger, error, `Error in getUserWorkspacesByTenantId for tenantId:${tenantId}`)
			workspaces = []
		}
		else if (!workspaces) {
			logger.warn(`No Workspaces found for tenantId:${tenantId}`)
			workspaces = []
		}

		return workspaces //.filter(w => w.tenant_id == tenantId || isTenantShared(w))  //return own workspaces & tenant shared in all other tenants
	}
	else {
		// eslint-disable-next-line prefer-const
		let { data: workspaces, error } = await supabase
			.from("workspaces")
			.select("*")
			.eq("tenant_id", tenantId)
			.order("created_at", { ascending: false })

		if (error) {
			logError(logger, error, `Error in getUserWorkspacesByTenantId for id:${tenantId}, msg:${error?.message}`)
			workspaces = []
		}
		else if (!workspaces) {
			logger.warn(`Attempt to getUserWorkspacesByTenantId for id:${tenantId} resulted in no content found`)
			workspaces = []
		}

		return workspaces
	}
}

export const getUserWorkspacesByTenantIdWithPermissions = async (
	supabase: SupabaseClient<Database>,
	authuser: Authuser,
	workspaceId: string
) : Promise<TableWithPermissions<'workspaces'>[]> => {
	const aidonAdmin = await isAidonAdmin(supabase, authuser)
	const tenantId = await authuser.getTenantId(supabase)
	const userWorkspaces = await getUserWorkspacesByTenantId(supabase, tenantId, aidonAdmin)
	const workspace = await getWorkspaceById(supabase, workspaceId)
	const result: TableWithPermissions<'workspaces'>[] = []

	for (const ws of userWorkspaces) {
		const perms = await evaluatePermissions(supabase, authuser,
				{ type: "workspaces", context: "ANY", uuid: ws.id, ws: workspace })

		{
			const options = { type: "workspaces", action: PermAction.edit, context: "tenant_shared",
					uuid: ws.id, ws: workspace } as PermOptions
			const allowed = await authCanPerform(supabase, authuser, options)
			const editProviderKey = permOptionsKey(options)
			perms[editProviderKey] = allowed
		}
		{
			const options = { type: "workspaces", action: PermAction.view, context: "tenant_shared",
					uuid: ws.id, ws: workspace } as PermOptions
			const allowed = await authCanPerform(supabase, authuser, options)
			const viewProviderKey = permOptionsKey(options)
			perms[viewProviderKey] = allowed
		}

		result.push({ ...ws, permissions: perms })
	}

  return result
}

export const getWorkspacesByUserId = async (supabase: SupabaseClient<Database>, userId: string) => {
  const { data: workspaces, error } = await supabase
    .from("workspaces")
    .select("*")
    .eq("user_id", userId)
    .order("created_at", { ascending: false })

  if (!workspaces) {
		logError(logger, error, 'Failed to fetch Workspaces by userId', { userId })
    throw new Error(error.message)
  }

  return workspaces
}

export const getAllWorkspaces = async (supabase: SupabaseClient<Database>) => {
	const { data: workspaces, error } = await supabase
		.from("workspaces")
		.select("*")

	if (!workspaces) {
		logError(logger, error, 'Failed to fetch all Workspaces')
		throw new Error(error.message)
	}

	return workspaces
}

export const createWorkspace = async (
	supabase: SupabaseClient<Database>,
  workspace: TablesInsert<"workspaces">
) => {
  const { data: createdWorkspace, error } = await supabase
    .from("workspaces")
    .insert([workspace])
    .select("*")
    .single()

  if (error) {
		logError(logger, error, 'Failed to create Workspace', { workspace })
    throw new Error(error.message)
  }

  logger.info('Workspace created successfully', { workspaceId: createdWorkspace.id })
  return createdWorkspace
}

export const updateWorkspace = async (
	supabase: SupabaseClient<Database>,
  workspaceId: string,
  workspace: TablesUpdate<"workspaces">
) => {
  const { data: updatedWorkspace, error } = await supabase
    .from("workspaces")
    .update(workspace)
    .eq("id", workspaceId)
    .select("*")
    .single()

  if (error) {
	logError(logger, error, 'Failed to update Workspace', { workspaceId, workspace })
    throw new Error(error.message)
  }

  return updatedWorkspace
}

export const deleteWorkspace = async (supabase: SupabaseClient<Database>, workspaceId: string) => {
  const { error } = await supabase
    .from("workspaces")
    .delete()
    .eq("id", workspaceId)

  if (error) {
	logError(logger, error, 'Failed to delete Workspace', { workspaceId })
    throw new Error(error.message)
  }

  return true
}

export const isTenantShared = (workspace: TablesUpdate<"workspaces"> | null) => {
	const wsMeta = workspace?.metadata as WorkspaceMetadata | undefined;
	return wsMeta?.tenant_shared === true
}

export const isRootWorkspace = (workspace: TablesUpdate<"workspaces"> | null) => {
	const wsMeta = workspace?.metadata as WorkspaceMetadata | undefined;
	return wsMeta?.root_workspace === true
}

export const setMetadataTenantShared = (
	metadata: Json,
	tenantShared: boolean
) => {
	const wsMeta: WorkspaceMetadata = metadata ? { ...metadata as WorkspaceMetadata } : {}
  wsMeta.tenant_shared = tenantShared;
	return wsMeta as Json
}

export const workspacesCanPerform = async (supabase: SupabaseClient<Database>, authuser: Authuser, options: PermOptions) => {
	if (authuser.isAuthorizedForRole(Role.root)) {
		return true
	}

	if (options.ws && authuser.isOwnWorkspace(options.ws)) {
		if (options.action == PermAction.view && options.uuid) {
			return isTenantShared(options.ws) ||
				await authuser.isAuthorizedForTable(supabase, TableType.workspace, options.uuid, Role.powerUser)
		}

		return true
	}

	switch (options.action) {
		case PermAction.view:
			if (options.ws && options.uuid) {
				return isTenantShared(options.ws) ||
					await authuser.isAuthorizedForTable(supabase, TableType.workspace, options.uuid, Role.powerUser)
			}
			return false
		case PermAction.select:
		case PermAction.edit:  //includes tenant_shared
		case PermAction.save:
		case PermAction.delete:
			if (options.uuid) {
				return await authuser.isAuthorizedForTable(supabase, TableType.workspace, options.uuid, Role.powerUser)
			}
			return false  //we need an id to confirm in this case
		default:
			if (authuser) {
				return authuser.isAuthorizedForRole(Role.admin)
			}
			return false
	}
}